import React from "react";
import { Card, CardBody, Col, Container, Row, Button, Alert } from "reactstrap";
import { useStoryGeneration } from "./hooks/useStoryGeneration";
import StoryForm from "./components/StoryForm";
import SceneTable from "./components/SceneTable";
import VideoResults from "./components/VideoResults";
import ProgressIndicator from "./components/ProgressIndicator";

const Stories = () => {
  const {
    prompt, setPrompt,
    numScenes, setNumScenes,
    selectedImage,
    mascotImage,
    loading,
    scenes, setScenes,
    error,
    regeneratingScene,
    generatingVideos,
    videoResults,
    videoStatuses,
    checkingStatus,
    showForm, setShowForm,
    currentStep,
    generatingSceneIndex,
    videoGenerationStep,
    videoProgress,
    editingScene, setEditingScene,
    editPrompt, setEditPrompt,
    regeneratingVideo,
    editingVideoPrompt, setEditingVideoPrompt,
    editVideoPrompt, setEditVideoPrompt,
    hasAllImages,
    handleImageUpload,
    generateStory,
    regenerateSceneImage,
    handleGenerateVideos,
    regenerateVideo,
    resetForm,
  } = useStoryGeneration();

  const handleEditPrompt = (sceneIndex) => {
    setEditingScene(sceneIndex);
    setEditPrompt(scenes[sceneIndex].description);
  };

  const handleSavePrompt = async (sceneIndex) => {
    const updatedScenes = [...scenes];
    updatedScenes[sceneIndex] = {
      ...updatedScenes[sceneIndex],
      description: editPrompt,
    };
    setScenes(updatedScenes);
    setEditingScene(null);
    setEditPrompt("");
    await regenerateSceneImage(sceneIndex);
  };

  const handleCancelEdit = () => {
    setEditingScene(null);
    setEditPrompt("");
  };

  const handleEditVideoPrompt = (sceneIndex) => {
    setEditingVideoPrompt(sceneIndex);
    setEditVideoPrompt(scenes[sceneIndex].video_prompt);
  };

  const handleSaveVideoPrompt = (sceneIndex) => {
    const updatedScenes = [...scenes];
    updatedScenes[sceneIndex] = {
      ...updatedScenes[sceneIndex],
      video_prompt: editVideoPrompt,
    };
    setScenes(updatedScenes);
    setEditingVideoPrompt(null);
    setEditVideoPrompt("");
  };

  const handleCancelVideoEdit = () => {
    setEditingVideoPrompt(null);
    setEditVideoPrompt("");
  };

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <Col sm={12}>
            <Card>
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">AI Story Generator</h5>
                <Button color="primary" onClick={resetForm} disabled={loading}>
                  <i className="fa fa-plus me-1"></i>
                  Create New Story
                </Button>
              </div>
              <CardBody>
                {error && (
                  <Alert color="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {showForm && (
                  <StoryForm
                    prompt={prompt}
                    setPrompt={setPrompt}
                    numScenes={numScenes}
                    setNumScenes={setNumScenes}
                    selectedImage={selectedImage}
                    mascotImage={mascotImage}
                    handleImageUpload={handleImageUpload}
                    onGenerate={() => {
                      generateStory();
                      setShowForm(false);
                    }}
                    loading={loading}
                  />
                )}

                {(loading || scenes.length > 0) && (
                  <div className="mt-4">
                    <ProgressIndicator
                      loading={loading}
                      currentStep={currentStep}
                      generatingSceneIndex={generatingSceneIndex}
                      numScenes={numScenes}
                      generatingVideos={generatingVideos}
                      videoGenerationStep={videoGenerationStep}
                      videoProgress={videoProgress}
                    />

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5>Generated Story Scenes</h5>
                      <Button color="secondary" size="sm" onClick={resetForm}>
                        <i className="fa fa-plus me-1"></i>
                        New Story
                      </Button>
                    </div>

                    <SceneTable
                      scenes={scenes}
                      editingScene={editingScene}
                      editPrompt={editPrompt}
                      setEditPrompt={setEditPrompt}
                      editingVideoPrompt={editingVideoPrompt}
                      editVideoPrompt={editVideoPrompt}
                      setEditVideoPrompt={setEditVideoPrompt}
                      regeneratingScene={regeneratingScene}
                      onEditPrompt={handleEditPrompt}
                      onSavePrompt={handleSavePrompt}
                      onCancelEdit={handleCancelEdit}
                      onEditVideoPrompt={handleEditVideoPrompt}
                      onSaveVideoPrompt={handleSaveVideoPrompt}
                      onCancelVideoEdit={handleCancelVideoEdit}
                      onRegenerateImage={regenerateSceneImage}
                    />

                    {hasAllImages && (
                      <div className="mt-3 text-center">
                        <Button
                          color="success"
                          size="lg"
                          onClick={handleGenerateVideos}
                          disabled={generatingVideos}
                        >
                          {generatingVideos ? (
                            <>
                              <i className="fa fa-spinner fa-spin me-2"></i>
                              Generating Videos...
                            </>
                          ) : (
                            <>
                              <i className="fa fa-video me-2"></i>
                              Generate Videos for All Scenes
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                    {videoResults && (
                      <VideoResults
                        videoResults={videoResults}
                        videoStatuses={videoStatuses}
                        scenes={scenes}
                        regeneratingVideo={regeneratingVideo}
                        checkingStatus={checkingStatus}
                        onRegenerateVideo={regenerateVideo}
                      />
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Stories;
